import React, { useState, useEffect, useRef } from 'react';
import { useProject } from '../../project-details/context/ProjectContext';
import { createProjectBearings } from '../../api/api';
import { isAuthenticated } from '../../../sso';
import ToggleButton from './ToggleButton';
import SaveToProjectDropDown from '../SaveToProjectDropDown';
import toast from 'react-hot-toast';
import { saveNewProjectClickAnalyticsHandler, saveDropdownSelectionAnalyticsHandler, saveDropDownOpenAnalyticsHandler } from '../../analytics';

type SaveBearingDropdownProps = {
	bearingTypeDescription?: string;
	bearingWidth?: string;
	bore?: string;
	marketPlaceDescription?: string;
	materialNumber?: string;
	outerDiameter?: string;
	bearingTypeImageUrl?: string;
	displayedUnit?: string;
	buttonStyle?: string;
	hideDropdown?: boolean;
	interactionType?: string;
};

const SaveBearingDropdown: React.FC<SaveBearingDropdownProps> = (initialProps) => {
	const { projects, refreshProjects, loading } = useProject();

	const [ isLoggedIn, setIsLoggedIn ] = useState(false);
	const [ isLoading, setIsLoading ] = useState(false);
	const [ isOpen, setIsOpen ] = useState(false);
	const dropdownRef = useRef<HTMLDivElement | null>(null);

	const toggleMenu = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
		saveDropDownOpenAnalyticsHandler(event, dropdownRef, isOpen, [ marketPlaceDescription || '' ], 'Add to Project', 'bearing part');
		setIsOpen((prev) => ! prev);
	};

	//bearing data
	const [ bearingTypeDescription, setBearingTypeDescription ] = useState(initialProps.bearingTypeDescription);
	const [ bearingWidth, setBearingWidth ] = useState(initialProps.bearingWidth);
	const [ bore, setBore ] = useState(initialProps.bore);
	const [ marketPlaceDescription, setMarketPlaceDescription ] = useState(initialProps.marketPlaceDescription);
	const [ materialNumber, setMaterialNumber ] = useState(initialProps.materialNumber);
	const [ outerDiameter, setOuterDiameter ] = useState(initialProps.outerDiameter);
	const [ displayedUnit, setDisplayedUnit ] = useState(initialProps.displayedUnit ?? 'M');
	const [ bearingTypeImageUrl, setBearingTypeImageUrl ] = useState(initialProps.bearingTypeImageUrl);
	const [ buttonStyle, setButtonStyle ] = useState(initialProps.buttonStyle);
	const [ hideDropdown, setHideDropdown ] = useState(initialProps.hideDropdown ?? false);

	// listen SSO account loaded
	useEffect(() => {
		setIsLoading(true);
		const handleAccountInfoLoaded = async () => {
			const isLogged = await isAuthenticated();
			setIsLoggedIn(isLogged);
			setIsLoading(false);
		};

		document.addEventListener('timken_sso_account_info_loaded', handleAccountInfoLoaded);

		return () => {
			document.removeEventListener('timken_sso_account_info_loaded', handleAccountInfoLoaded);
		};
	}, []);

	useEffect(() => {
		if (isLoggedIn) {
			setIsLoading(loading);
		}
	}, [ loading, isLoggedIn ]);

	// listen bearingSelected custom event
	useEffect(() => {
		const handleBearingSelected = (e: Event) => {
			const customEvent = e as CustomEvent<any>;
			const data = customEvent.detail;

			setBearingTypeDescription(data.bearingTypeDescription);
			setBearingWidth(data.bearingWidth);
			setBore(data.bore);
			setMarketPlaceDescription(data.marketPlaceDescription);
			setMaterialNumber(data.materialNumber);
			setOuterDiameter(data.outerDiameter);
			setDisplayedUnit(data.unit);

			// set hideDropdown if available; otherwise, assume is false
			if (typeof data.hideDropdown !== 'undefined') {
				setHideDropdown(data.hideDropdown);
			} else {
				setHideDropdown(false);
			}

			// get bearing type image
			const bearingTypeThumbnail = window._timken.bearingTypeThumbnails[ data.partType ] || '';
			setBearingTypeImageUrl(bearingTypeThumbnail);
		};

		document.addEventListener('bearingSelectedEvent', handleBearingSelected);

		return () => {
			document.removeEventListener('bearingSelectedEvent', handleBearingSelected);
		};
	}, []);

	/**
	 * Close dropdown if user clicks outside of dropdown container
	 */
	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (dropdownRef.current && ! dropdownRef.current.contains(event.target as Node)) {
				setIsOpen(false);
			}
		};

		if (isOpen) {
			document.addEventListener('click', handleClickOutside);
		}

		return () => document.removeEventListener('click', handleClickOutside);
	}, [ isOpen ]);

	/**
	 * Refresh project data when a new project is created
	 *
	 * @param  createdProjectId
	 */
	const onNewProjectCreated = async (createdProjectId) => {
		setIsOpen(false);
		saveNewProjectClickAnalyticsHandler(dropdownRef, 'Add to Project', 'bearing part');
		await saveBearingToProject(createdProjectId); // save bearing to the created project
	};

	/**
	 * Save bearing to project
	 *
	 * @param  projectId
	 */
	const saveBearingToProject = async (projectId: string) => {
		if (! projectId) {
			return;
		}

		try {
			setIsLoading(true);
			const toastId = toast.loading('Loading...');

			const payload = {
				projectId,
				displayedUnit,
				bearings: [ {
					materialNumber,
					marketPlaceDescription,
					bearingTypeDescription,
					bearingTypeImageUrl,
					bore,
					outerDiameter,
					bearingWidth,
				} ],
			};

			await createProjectBearings(payload);
			toast.success(`${ marketPlaceDescription } added to project`, {
				id: toastId,
			});
			setIsLoading(false);
			refreshProjects();
		} catch (error) {
			setIsLoading(false);
			toast.dismiss();
			toast.error(error.message);
		}
	};

	const trackSaveDropdownSelection = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
		saveDropdownSelectionAnalyticsHandler(event, dropdownRef, 'Add to Project', 'bearing part');
	};
	const handleSaveBearingsClick = (event: React.MouseEvent<HTMLElement, MouseEvent>, projectId: string) => {
		saveDropdownSelectionAnalyticsHandler(event, dropdownRef, 'Add to Project', 'bearing part');

		saveBearingToProject(projectId); // save bearing
		setIsOpen(false);
	};

	// not render anything if hideDropdown is true
	if (hideDropdown) {
		return null;
	}

	return (
		<div className={`dropdown ${ isLoading ? 't-full-block-skeleton' : '' }`} ref={dropdownRef} data-item-number={marketPlaceDescription} data-project-count={projects.length}>
			<ToggleButton
				buttonStyle={buttonStyle}
				isOpen={isOpen}
				isLoggedIn={isLoggedIn}
				marketPlaceDescription={marketPlaceDescription}
				toggleMenu={(event: Event) => {
					toggleMenu(event);
				}}
			/>

			<SaveToProjectDropDown
				projects={projects}
				isOpen={isOpen}
				targetType='bearings'
				onSuccess={onNewProjectCreated}
				onModalOpen={trackSaveDropdownSelection}
				projectItemCompareValue={marketPlaceDescription || ''}
				onSaveClick={handleSaveBearingsClick}>
			</SaveToProjectDropDown>
		</div>
	);
};

export default SaveBearingDropdown;
